/**
 * On-match editor for a v2 intent (NLU P1 plan C1): what hub tells v2 to send when this intent matches — a v2 block
 * per channel (picked from v2's real block list), a reply text, or nothing. The server validates every block against
 * the bot's list; this editor never lets a free-typed id through.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { hubService, type V2Block } from '../../services/hub.service';
import { isComplete, PICKER_CHANNELS, type OnMatch, type V2Channel } from '../../lib/onMatchView';

type Props = {
  botId: string;
  intentCode: string;
  value: OnMatch | null | undefined;
  onSaved: () => void;
  onCancel: () => void;
};

export function OnMatchEditor({ botId, intentCode, value, onSaved, onCancel }: Props) {
  const { getToken } = useAuth();
  const [draft, setDraft] = useState<OnMatch>(value ?? { type: 'none' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await hubService.updateIntent(botId, intentCode, { onMatch: draft }, 'chatbot_v2', getToken());
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const setType = (type: OnMatch['type']) =>
    setDraft(
      type === 'reply'
        ? { type, text: draft.type === 'reply' ? draft.text : '' }
        : type === 'block'
          ? { type, blocks: draft.type === 'block' ? draft.blocks : {} }
          : { type: 'none' },
    );

  return (
    <div className="space-y-3 rounded border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap gap-4 text-sm">
        {(['block', 'reply', 'none'] as const).map((t) => (
          <label key={t} className="flex items-center gap-1">
            <input type="radio" name={`om-${intentCode}`} checked={draft.type === t} onChange={() => setType(t)} />
            {t === 'block' ? 'Send a v2 block' : t === 'reply' ? 'Reply with text' : 'Nothing (v2 answers as before)'}
          </label>
        ))}
      </div>

      {draft.type === 'reply' && (
        <textarea
          value={draft.text}
          maxLength={2000}
          onChange={(e) => setDraft({ type: 'reply', text: e.target.value })}
          rows={3}
          placeholder="Text v2 sends when this intent matches"
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
        />
      )}

      {draft.type === 'block' && (
        <div className="space-y-2">
          {PICKER_CHANNELS.map((ch) => (
            <BlockPicker
              key={ch}
              botId={botId}
              channel={ch}
              selected={draft.blocks[ch] ?? null}
              onChange={(b) => {
                const blocks = { ...draft.blocks };
                if (b) blocks[ch] = { id: b.id, name: b.name, checkedAt: '' };
                else delete blocks[ch];
                setDraft({ type: 'block', blocks });
              }}
            />
          ))}
          <p className="text-xs text-gray-500">Customers on a channel without a block get v2's normal answer.</p>
        </div>
      )}

      {error && <div className="text-xs text-red-600">{error}</div>}
      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={saving || !isComplete(draft)}
          className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white">
          Cancel
        </button>
      </div>
    </div>
  );
}

function BlockPicker({
  botId,
  channel,
  selected,
  onChange,
}: {
  botId: string;
  channel: V2Channel;
  selected: { id: number; name: string } | null;
  onChange: (b: { id: number; name: string } | null) => void;
}) {
  const { getToken } = useAuth();
  const [q, setQ] = useState('');
  const [list, setList] = useState<V2Block[]>([]);
  const [available, setAvailable] = useState(true);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      hubService
        .listV2Blocks(botId, channel, q, getToken())
        .then((r) => {
          if (!alive) return;
          setAvailable(r.available);
          setList(r.blocks);
        })
        .catch(() => alive && setAvailable(false));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [botId, channel, q, getToken]);

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="w-24 text-xs font-medium text-gray-600">{channel}</span>
      {!available ? (
        <span className="text-xs text-amber-700">v2 block list not available yet</span>
      ) : (
        <>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search blocks"
            className="w-40 rounded border border-gray-300 px-2 py-1 text-xs"
          />
          <select
            value={selected?.id ?? ''}
            onChange={(e) => {
              const id = Number(e.target.value);
              const b = list.find((x) => x.id === id);
              onChange(b ? { id: b.id, name: b.name } : null);
            }}
            className="min-w-[14rem] rounded border border-gray-300 px-2 py-1 text-xs"
          >
            <option value="">— none on this channel —</option>
            {selected && !list.some((b) => b.id === selected.id) && <option value={selected.id}>{selected.name}</option>}
            {list.map((b) => (
              <option key={b.id} value={b.id}>
                {b.folder ? `${b.folder} / ` : ''}
                {b.name}
                {b.general ? ' (general)' : ''}
                {b.greeting ? ' (greeting)' : ''}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  );
}
